import React, { useState, useEffect } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import { fetchData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import RefreshIcon from '@mui/icons-material/Refresh';
import FeedIcon from '@mui/icons-material/Feed';
import CopyrightIcon from '@mui/icons-material/Copyright';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import '../shared.css';

function PurchaseSummary() {
    const [dataItem, setDataItem] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [showSearchInput, setShowSearchInput] = useState(false);
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const { isAuthenticated, companyId, branchId, permissions, token, logout } = useAuth();
    const [totalqty, setTotalqty] = useState(0);
    const [totalamt, setTotalamt] = useState(0);
    const [totalgst, setTotalgst] = useState(0);
    const [totalnet, setTotalnet] = useState(0);
    const [totalbills, setTotalbills] = useState(0);

    useEffect(() => {
        if (isAuthenticated && tokenIsExpired(token)) { logout(); return; }
        setFromDate(getFormattedStartDate());
        setToDate(getFormattedCurrentDate());
        fetchTableData();
    }, [isAuthenticated, companyId, branchId, token, logout]);

    useEffect(() => {
        let totalqty = 0, totalamt = 0, totalgst = 0, totalnet = 0;
        let billNumbers = new Set();
        dataItem.forEach(item => {
            totalqty += item.Quantity;
            totalamt += item.Amount;
            totalgst += item.GSTAmount;
            totalnet += item.TotalAmount;
            billNumbers.add(item.GRNNumber);
        });
        setTotalqty(totalqty);
        setTotalamt(totalamt);
        setTotalgst(totalgst);
        setTotalnet(totalnet);
        setTotalbills(billNumbers.size);
    }, [dataItem]);

    const getFormattedCurrentDate = () => new Date().toISOString().split('T')[0];
    const getFormattedStartDate = () => { const d = new Date(); d.setDate(1); return d.toISOString().split('T')[0]; };
    const handleFromDateChange = (e) => setFromDate(e.target.value);
    const handleToDateChange = (e) => setToDate(e.target.value);

    const hasReadReportPermission = permissions.includes('Read Reports');
    const [loading, setLoading] = useState(false);

    const fetchTableData = async () => {
        if (hasReadReportPermission) {
            setDataItem([]);
            setLoading(true);
            try {
                const response = await fetchData('ReportApi', `/PurchaseSummary/${companyId}/${branchId}/${fromDate}/${toDate}`, token);
                if (response.responseCode === '1111') setDataItem(response.response_data);
            } catch (error) {
                console.error('Error fetching Purchase Summary Details:', error);
            } finally {
                setLoading(false);
            }
        }
    };

    const filteredDataItem = dataItem.filter((item) =>
        Object.values(item).some((value) => String(value).toLowerCase().includes(searchQuery.toLowerCase()))
    );

    const formatDate = (dateString) => new Date(dateString).toLocaleDateString('en-GB', { year: 'numeric', month: '2-digit', day: '2-digit' });

    const handleExportExcel = async () => {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Purchase Summary');
        const rightAlignColumns = ['Quantity', 'Purchase Rate', 'GST %', 'Net Value', 'GST Amount', 'Net Value + Tax'];
        const headerCaption = `Purchase Summary From : ${formatDate(fromDate)} To : ${formatDate(toDate)}`;
        worksheet.mergeCells('A1:W1');
        const headerCaptionCell = worksheet.getCell('A1');
        headerCaptionCell.value = headerCaption;
        headerCaptionCell.font = { bold: true, size: 14 };
        headerCaptionCell.alignment = { vertical: 'middle', horizontal: 'left' };
        const headerRowValues = ['Purchase Type', 'GRN Date', 'GRN Number', 'LR Date', 'LR Number', 'PG', 'IG', 'ISG', 'PF', 'Item No', 'Description', 'HSN Code', 'Quantity', 'Net Value', 'GST %', 'GST Amount', 'Net Value + Tax', 'Comments', 'Category', 'Item Group', 'Season Code', 'Size Code'];
        const headerRow = worksheet.addRow(headerRowValues);
        headerRow.eachCell((cell) => {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFB6D7A8' } };
            cell.font = { bold: true, color: { argb: 'FF000000' } };
            cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
            cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
        });
        worksheet.columns = headerRowValues.map(() => ({ width: 18 }));
        filteredDataItem.forEach((rowData) => {
            const row = worksheet.addRow([rowData.PurchaseType, formatDate(rowData.GRNDate), rowData.GRNNumber, formatDate(rowData.LRDate), rowData.LRNumber, rowData.PG, rowData.IG, rowData.ISG, rowData.PF, parseFloat(rowData.Code), rowData.ItemName, rowData.HSNCode, parseFloat(rowData.Quantity), parseFloat(rowData.Amount.toFixed(2)), parseFloat(rowData.GSTRate), parseFloat(rowData.GSTAmount.toFixed(2)), parseFloat(rowData.TotalAmount.toFixed(2)), rowData.Remarks, rowData.CategoryName, rowData.ItemGroupName, rowData.SeasonCode, rowData.SizeCode]);
            row.eachCell((cell, colNumber) => {
                if (rightAlignColumns.includes(headerRowValues[colNumber - 1])) cell.alignment = { vertical: 'middle', horizontal: 'right' };
                else cell.alignment = { vertical: 'middle', horizontal: 'center' };
                cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
            });
        });
        const totalRow = worksheet.addRow(['', '', '', '', '', '', '', '', '', '', 'Total Bills', totalbills, totalqty, totalamt, '', totalgst, totalnet, '', '', '', '', '']);
        totalRow.font = { bold: true };
        totalRow.eachCell((cell, colNumber) => {
            if (rightAlignColumns.includes(headerRowValues[colNumber - 1])) cell.alignment = { vertical: 'middle', horizontal: 'right' };
            else cell.alignment = { vertical: 'middle', horizontal: 'center' };
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFF2CC' } };
            cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
        });
        worksheet.views = [{ state: 'frozen', ySplit: 2 }];
        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer]), 'PurchaseSummary.xlsx');
    };

    return (
        <div className="pg-container">
            <div className="pg-header">
                <h5 className="pg-title">Purchase Summary</h5>
                <div className="pg-header-actions">
                    <div className="pg-date-row">
                        <div className="pg-field">
                            <label className="pg-label" style={{ background: 'var(--pg-bg)' }}>From Date</label>
                            <input className="pg-input" type="date" value={fromDate} onChange={handleFromDateChange} />
                        </div>
                        <div className="pg-field">
                            <label className="pg-label" style={{ background: 'var(--pg-bg)' }}>To Date</label>
                            <input className="pg-input" type="date" value={toDate} onChange={handleToDateChange} />
                        </div>
                        <button className="pg-btn pg-btn-outline" onClick={fetchTableData} disabled={loading}>
                            <RefreshIcon /> Refresh
                        </button>
                    </div>
                    <div className="pg-action-row">
                        <button className="pg-btn pg-btn-success" onClick={handleExportExcel}><FeedIcon /> Excel</button>
                        <button className="pg-btn pg-btn-outline" onClick={() => setShowSearchInput(v => !v)}><SearchIcon /> Search</button>
                    </div>
                    {showSearchInput && (
                        <input className="pg-input pg-search-input" placeholder="Search…"
                            value={searchQuery} onChange={e => setSearchQuery(e.target.value)} autoFocus />
                    )}
                </div>
            </div>

            {loading && (
                <div className="pg-loading-overlay">
                    <div className="pg-loading-card">
                        <div className="pg-logo-loader">
                            <div className="pg-logo-block pg-block-green"></div>
                            <div className="pg-logo-block pg-block-blue"></div>
                            <div className="pg-logo-block pg-block-red"></div>
                            <div className="pg-logo-block pg-block-yellow"></div>
                        </div>
                        <p className="pg-loading-text">Loading...</p>
                    </div>
                </div>
            )}

            <div className="pg-table-wrap">
                <table className="pg-table">
                    <thead>
                        <tr>
                            <th>Purchase Type</th>
                            <th>GRN Date</th>
                            <th>GRN Number</th>
                            <th>LR Date</th>
                            <th>LR Number</th>
                            <th>PG</th>
                            <th>IG</th>
                            <th>ISG</th>
                            <th>PF</th>
                            <th>Item No</th>
                            <th>Description</th>
                            <th>HSN Code</th>
                            <th style={{ textAlign: 'right' }}>Quantity</th>
                            <th style={{ textAlign: 'right' }}>Net Value</th>
                            <th style={{ textAlign: 'right' }}>GST %</th>
                            <th style={{ textAlign: 'right' }}>GST Amount</th>
                            <th style={{ textAlign: 'right' }}>Net Value + GST</th>
                            <th>Comments</th>
                            <th>Category</th>
                            <th>Item Group</th>
                            <th>Season Code</th>
                            <th>Size Code</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredDataItem.length === 0
                            ? <tr><td colSpan={22} style={{ textAlign: 'center', color: '#94a3b8', padding: 16 }}>No records found</td></tr>
                            : filteredDataItem.map((data, index) => (
                                <tr key={index}>
                                    <td>{data.PurchaseType}</td>
                                    <td>{formatDate(data.GRNDate)}</td>
                                    <td>{data.GRNNumber}</td>
                                    <td>{formatDate(data.LRDate)}</td>
                                    <td>{data.LRNumber}</td>
                                    <td>{data.PG}</td>
                                    <td>{data.IG}</td>
                                    <td>{data.ISG}</td>
                                    <td>{data.PF}</td>
                                    <td>{data.Code}</td>
                                    <td>{data.ItemName}</td>
                                    <td>{data.HSNCode}</td>
                                    <td style={{ textAlign: 'right' }}>{data.Quantity}</td>
                                    <td style={{ textAlign: 'right' }}>{data.Amount.toFixed(2)}</td>
                                    <td style={{ textAlign: 'right' }}>{data.GSTRate}</td>
                                    <td style={{ textAlign: 'right' }}>{data.GSTAmount.toFixed(2)}</td>
                                    <td style={{ textAlign: 'right' }}>{data.TotalAmount.toFixed(2)}</td>
                                    <td>{data.Remarks}</td>
                                    <td>{data.CategoryName}</td>
                                    <td>{data.ItemGroupName}</td>
                                    <td>{data.SeasonCode}</td>
                                    <td>{data.SizeCode}</td>
                                </tr>
                            ))
                        }
                        <tr style={{ background: 'linear-gradient(135deg, #1c2a3a 0%, #243447 100%)', color: '#e8f0f8', fontWeight: 700 }}>
                            <td colSpan={10}></td>
                            <td style={{ padding: '6px 10px' }}>Total Bills</td>
                            <td style={{ padding: '6px 10px' }}>{totalbills}</td>
                            <td style={{ textAlign: 'right', padding: '6px 10px' }}>{totalqty}</td>
                            <td style={{ textAlign: 'right', padding: '6px 10px' }}>{totalamt.toFixed(2)}</td>
                            <td></td>
                            <td style={{ textAlign: 'right', padding: '6px 10px' }}>{totalgst.toFixed(2)}</td>
                            <td style={{ textAlign: 'right', padding: '6px 10px' }}>{totalnet.toFixed(2)}</td>
                            <td colSpan={5}></td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div className="pg-watermark"><CopyrightIcon style={{ fontSize: 12 }} /><b>EFFTABS SOLUTIONS LLP</b></div>
        </div>
    );
}

export default PurchaseSummary;
